/**
 * Provider Directory Page
 * Main page that integrates search, results, map, detail views,
 * default provider listing, category filters, and AI smart search.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ProviderSearch } from '@/components/provider-directory/ProviderSearch';
import { ProviderResults, Provider } from '@/components/provider-directory/ProviderResults';
import { ProviderMap } from '@/components/provider-directory/ProviderMap';
import { ProviderDetail } from '@/components/provider-directory/ProviderDetail';
import { providerDirectoryAPI, SearchFilters, BrowseResponse, CategoriesResponse, SmartSearchResponse } from '@/services/providerDirectoryApi';
import {
  Map, List, X, CheckCircle, Search, Sparkles, Building2, MapPin,
  ChevronLeft, ChevronRight, Filter, Globe, Tag, Database, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/ErrorState';
import { config } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface ProviderDirectoryProps {
  onBack?: () => void;
  onSelectProvider?: (provider: Provider) => void;
  showSelectButton?: boolean;
}

type SearchMode = 'browse' | 'location' | 'smart';

// Searchable filter combobox — replaces plain <select> with typeahead
function SearchableFilter({ label, value, onChange, options, placeholder }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; count: number }>;
  placeholder?: string;
}) {
  const [filterText, setFilterText] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on click outside
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = filterText
    ? options.filter(o => o.label.toLowerCase().includes(filterText.toLowerCase()))
    : options;

  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <div
        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? '' : 'text-gray-400'}>{selectedLabel || `All ${label}s`}</span>
        {value && (
          <button onClick={(e) => { e.stopPropagation(); onChange(''); setFilterText(''); }} className="text-gray-400 hover:text-gray-600 ml-1">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-1.5 border-b border-gray-200 dark:border-gray-700">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={placeholder || 'Type to filter...'}
              className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-purple-500"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <div
              className="px-2.5 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              onClick={() => { onChange(''); setFilterText(''); setIsOpen(false); }}
            >
              All {label}s
            </div>
            {filtered.slice(0, 50).map(opt => (
              <div
                key={opt.value}
                className={`px-2.5 py-1.5 text-sm cursor-pointer flex justify-between items-center ${
                  opt.value === value
                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-medium'
                    : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                onClick={() => { onChange(opt.value); setFilterText(''); setIsOpen(false); }}
              >
                <span className="truncate mr-2">{opt.label}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{opt.count.toLocaleString()}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-2.5 py-3 text-sm text-gray-400 text-center">No matches</div>
            )}
            {filtered.length > 50 && (
              <div className="px-2.5 py-1.5 text-xs text-gray-400 text-center">Type to narrow {filtered.length} options...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProviderDirectory({ onSelectProvider, showSelectButton = false }: ProviderDirectoryProps = {}) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [searchLocation, setSearchLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [myProviders, setMyProviders] = useState<Provider[]>([]);
  const [showSavedNotification, setShowSavedNotification] = useState(false);

  // Browse/listing state
  const [searchMode, setSearchMode] = useState<SearchMode>('browse');
  const [browsePage, setBrowsePage] = useState(1);
  const [browseTotal, setBrowseTotal] = useState(0);         // filtered count from current browse query
  const [browseOverallTotal, setBrowseOverallTotal] = useState(0); // total providers in entire directory
  const [browseTotalPages, setBrowseTotalPages] = useState(1);
  const [browseCategories, setBrowseCategories] = useState<Array<{ name: string; count: number }>>([]);
  const [browseCoverage, setBrowseCoverage] = useState<Record<string, number>>({});

  // Category/filter state
  const [categories, setCategories] = useState<CategoriesResponse | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [selectedOrgType, setSelectedOrgType] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');

  // Smart search state
  const [smartQuery, setSmartQuery] = useState('');
  const [smartSearchResult, setSmartSearchResult] = useState<SmartSearchResponse | null>(null);
  const [isSmartSearching, setIsSmartSearching] = useState(false);

  // Load categories and stats on mount, refresh every 5 minutes for live count
  useEffect(() => {
    fetchSavedProviders();
    loadCategories();
    const interval = setInterval(loadCategories, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadCategories = async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const cats = await providerDirectoryAPI.getCategories();
      setCategories(cats);
      // Populate browse categories and coverage from categories response
      if (cats.organization_types.length > 0) {
        setBrowseCategories(cats.organization_types.slice(0, 20));
      }
      if (cats.states.length > 0) {
        const coverage: Record<string, number> = {};
        cats.states.forEach(s => { coverage[s.state] = s.count; });
        setBrowseCoverage(coverage);
      }
      if (cats.total_providers) {
        setBrowseOverallTotal(cats.total_providers);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategoriesError(error instanceof Error ? error.message : 'Failed to load directory metrics');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadBrowseProviders = useCallback(async (page: number, orgType?: string, service?: string, state?: string, sort?: string, geocodedOnly?: boolean) => {
    setIsSearching(true);
    setSearchError(null);

    try {
      const result = await providerDirectoryAPI.browse({
        page,
        pageSize: 50,
        organizationType: orgType || undefined,
        services: service || undefined,
        state: state || undefined,
        sortBy: sort || sortBy,
        // Map view requests only providers with coordinates so it renders pins
        // instead of silently dropping un-geocoded records.
        geocodedOnly: geocodedOnly ?? viewMode === 'map',
      });

      setProviders(result.providers);
      setBrowseTotal(result.total_count);
      setBrowseTotalPages(result.total_pages);
      setBrowsePage(result.page);
      setBrowseCategories(result.categories);
      setBrowseCoverage(result.coverage_summary);
    } catch (error) {
      console.error('Browse error:', error);
      setSearchError(error instanceof Error ? error.message : 'Failed to load providers');
      setProviders([]);
    } finally {
      setIsSearching(false);
    }
  }, [sortBy, viewMode]);

  // When the user toggles between list and map in browse mode, reload the
  // current page with the appropriate dataset — map view needs only providers
  // that have coordinates, otherwise it would render empty for pages dominated
  // by un-geocoded records.
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    if (prevViewModeRef.current !== viewMode) {
      prevViewModeRef.current = viewMode;
      if (searchMode === 'browse' && (providers.length > 0 || browsePage > 1)) {
        loadBrowseProviders(1, selectedOrgType, selectedService, selectedState, sortBy, viewMode === 'map');
      }
    }
  }, [viewMode, searchMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSavedProviders = async () => {
    try {
      const response = await authFetch(`${config.api.baseUrl}/api/saved-providers`);

      if (response.ok) {
        const data = await response.json();
        setMyProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error loading saved providers:', error);
    }
  };

  // Save a provider to "My Providers"
  const handleSaveProvider = async (provider: Provider) => {
    const isAlreadySaved = myProviders.some(p => p.id === provider.id);

    if (!isAlreadySaved) {
      setMyProviders(prev => [...prev, provider]);

      try {
        const providerData = {
          provider_id: provider.id,
          organization_name: provider.organization_name,
          organization_type: provider.organization_type,
          street_address: provider.street_address,
          city: provider.city,
          state: provider.state,
          zip_code: provider.zip_code,
          primary_phone: provider.primary_phone,
          primary_email: provider.primary_email,
          website: provider.website,
          services: Array.isArray(provider.services) ? provider.services.join(', ') : (provider.services || ''),
          languages_spoken: Array.isArray(provider.languages_spoken) ? provider.languages_spoken.join(', ') : (provider.languages_spoken || ''),
        };

        const response = await authFetch(`${config.api.baseUrl}/api/saved-providers`, {
          method: 'POST',
          jsonBody: providerData
        });

        if (response.ok) {
          await fetchSavedProviders();
          setShowSavedNotification(true);
          setTimeout(() => setShowSavedNotification(false), 3000);
        } else if (response.status !== 409) {
          setMyProviders(prev => prev.filter(p => p.id !== provider.id));
        }
      } catch (error) {
        console.error('Error saving provider:', error);
        setMyProviders(prev => prev.filter(p => p.id !== provider.id));
      }
    }
  };

  const isProviderSaved = (providerId: string) => {
    return myProviders.some(p => p.id === providerId);
  };

  // Location-based search
  const handleSearch = async (location: string, radiusMiles: number, filters: SearchFilters) => {
    setIsSearching(true);
    setSearchError(null);
    setSearchMode('location');
    setSmartSearchResult(null);

    try {
      const response = await providerDirectoryAPI.search(location, radiusMiles, filters);
      setProviders(response.providers);

      if (response.providers.length > 0 && response.providers[0].latitude && response.providers[0].longitude) {
        setSearchLocation({
          lat: response.providers[0].latitude,
          lng: response.providers[0].longitude,
        });
      }

      if (response.providers.length > 0) {
        setViewMode('map');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error instanceof Error ? error.message : 'An error occurred while searching');
      setProviders([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Unified search — routes to /search/unified which auto-detects query type
  // Also respects active sidebar filters
  const handleSmartSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smartQuery.trim()) return;

    setIsSmartSearching(true);
    setSearchError(null);
    setSearchMode('smart');

    try {
      // Build query that includes active filters
      let effectiveQuery = smartQuery.trim();
      // If user selected a state filter but didn't include it in query, append it
      if (selectedState && !effectiveQuery.toLowerCase().includes(selectedState.toLowerCase())) {
        effectiveQuery += ` ${selectedState}`;
      }

      const result = await providerDirectoryAPI.unifiedSearch(effectiveQuery);

      // Apply client-side org type filter if set (backend unified search doesn't have this param)
      let filtered = result.providers;
      if (selectedOrgType) {
        filtered = filtered.filter(p => p.organization_type === selectedOrgType);
      }
      if (selectedService) {
        filtered = filtered.filter(p =>
          p.services && JSON.stringify(p.services).toLowerCase().includes(selectedService.toLowerCase())
        );
      }

      setProviders(filtered);
      setSmartSearchResult({ ...result, providers: filtered, total_count: filtered.length });

      // Switch to map view if results have coordinates
      if (filtered.length > 0 && filtered[0].latitude && filtered[0].longitude) {
        setSearchLocation({
          lat: filtered[0].latitude,
          lng: filtered[0].longitude,
        });
        setViewMode('map');
      } else {
        setViewMode('list');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error instanceof Error ? error.message : 'Search failed');
      setProviders([]);
    } finally {
      setIsSmartSearching(false);
    }
  };

  // Filter handlers for browse mode
  const handleFilterChange = (type: 'orgType' | 'service' | 'state' | 'sort', value: string) => {
    const newOrgType = type === 'orgType' ? value : selectedOrgType;
    const newService = type === 'service' ? value : selectedService;
    const newState = type === 'state' ? value : selectedState;
    const newSort = type === 'sort' ? value : sortBy;

    if (type === 'orgType') setSelectedOrgType(value);
    if (type === 'service') setSelectedService(value);
    if (type === 'state') setSelectedState(value);
    if (type === 'sort') setSortBy(value);

    // If there's an active search query, re-run it with the new filters
    if (smartQuery.trim() && searchMode === 'smart') {
      // Trigger re-search with updated filters (via useEffect or direct call)
      setTimeout(() => {
        const form = document.querySelector('form');
        if (form) form.requestSubmit();
      }, 0);
      return;
    }

    setSearchMode('browse');
    setSmartSearchResult(null);
    loadBrowseProviders(1, newOrgType, newService, newState, newSort);
  };

  const clearAllFilters = () => {
    setSelectedOrgType('');
    setSelectedService('');
    setSelectedState('');
    setSortBy('name');
    setSearchMode('browse');
    setSmartSearchResult(null);
    setSmartQuery('');
    loadBrowseProviders(1, '', '', '', 'name');
  };

  const handleProviderClick = (provider: Provider) => {
    setSelectedProvider(provider);
  };

  const handleCloseDetail = () => {
    setSelectedProvider(null);
  };

  const handleGetDirections = (provider: Provider) => {
    const destination = `${provider.street_address}, ${provider.city}, ${provider.state} ${provider.zip_code}`;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`, '_blank');
  };

  const handlePageChange = (newPage: number) => {
    setBrowsePage(newPage);
    loadBrowseProviders(newPage, selectedOrgType, selectedService, selectedState, sortBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = selectedOrgType || selectedService || selectedState;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
            <div>
              <p className="text-[13px] text-ink-500">
                {categoriesLoading && browseOverallTotal === 0 ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading directory metrics…
                  </span>
                ) : categoriesError && browseOverallTotal === 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-amber-600 dark:text-amber-400">Unable to load directory metrics.</span>
                    <button
                      onClick={loadCategories}
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Retry
                    </button>
                  </span>
                ) : (
                  <>Browse {browseOverallTotal > 0 ? browseOverallTotal.toLocaleString() : ''} community-based healthcare and social service providers</>
                )}
              </p>
            </div>
            {/* Coverage badges */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(browseCoverage).length > 0 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  <Globe className="w-3 h-3 mr-1" />
                  {Object.keys(browseCoverage).length} States
                </span>
              )}
              {browseCategories.length > 0 && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                  <Tag className="w-3 h-3 mr-1" />
                  {browseCategories.length} Categories
                </span>
              )}
            </div>
          </div>

          {/* Unified Search Box */}
          <div className="mt-4">
            <form onSubmit={handleSmartSearch}>
              <div className="relative flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={smartQuery}
                    onChange={(e) => setSmartQuery(e.target.value)}
                    placeholder='Search by name, city, ZIP, or phrase — e.g. "Kaiser", "Fremont, CA", "94538", "food banks near Houston"'
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSmartSearching || !smartQuery.trim()}
                  loading={isSmartSearching}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 md:px-6 rounded-lg flex items-center gap-2 whitespace-nowrap"
                >
                  <Search className="w-4 h-4" />
                  <span className="hidden sm:inline">Search</span>
                </Button>
                {(searchMode !== 'browse' || smartQuery) && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Smart search interpretation */}
          {smartSearchResult && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-purple-600 dark:text-purple-400 font-medium">Understood:</span>
              {smartSearchResult.interpreted_query.state && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                  State: {smartSearchResult.interpreted_query.state}
                </span>
              )}
              {smartSearchResult.interpreted_query.services?.map((s: string) => (
                <span key={s} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                  {s}
                </span>
              ))}
              {smartSearchResult.interpreted_query.organization_type && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                  Type: {smartSearchResult.interpreted_query.organization_type}
                </span>
              )}
              {smartSearchResult.interpreted_query.language && (
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                  Lang: {smartSearchResult.interpreted_query.language}
                </span>
              )}
              {smartSearchResult.interpreted_query.accepts_medicaid && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">Medicaid</span>
              )}
              {smartSearchResult.interpreted_query.accepts_medicare && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">Medicare</span>
              )}
              {smartSearchResult.interpreted_query.text_terms && (
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                  &quot;{smartSearchResult.interpreted_query.text_terms}&quot;
                </span>
              )}
              <span className="text-gray-400 ml-1">
                {smartSearchResult.total_count} results in {smartSearchResult.search_time_ms}ms
              </span>
            </div>
          )}
          {/* Suggestions */}
          {smartSearchResult && smartSearchResult.suggestions.length > 0 && smartSearchResult.total_count < 5 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
              <span className="text-gray-500 dark:text-gray-400">Suggestions:</span>
              {smartSearchResult.suggestions.map((s, i) => (
                <span key={i} className="text-purple-600 dark:text-purple-400">{s}</span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* Left Sidebar - Filters & Categories */}
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-4 space-y-4">
              {/* Category Filters */}
              <Card className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                      <Filter className="w-4 h-4" />
                      Filters
                    </h3>
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {/* Organization Type — searchable */}
                    <SearchableFilter
                      label="Organization Type"
                      value={selectedOrgType}
                      onChange={(v) => handleFilterChange('orgType', v)}
                      options={(categories?.organization_types || browseCategories).map(cat => ({
                        value: cat.name, label: cat.name, count: cat.count
                      }))}
                      placeholder="Search types..."
                    />

                    {/* Service Category — searchable */}
                    <SearchableFilter
                      label="Service Category"
                      value={selectedService}
                      onChange={(v) => handleFilterChange('service', v)}
                      options={(categories?.service_categories || []).map(svc => ({
                        value: svc.name, label: svc.name, count: svc.count
                      }))}
                      placeholder="Search services..."
                    />

                    {/* State — searchable */}
                    <SearchableFilter
                      label="State"
                      value={selectedState}
                      onChange={(v) => handleFilterChange('state', v)}
                      options={(categories?.states || []).map(st => ({
                        value: st.state, label: st.state, count: st.count
                      }))}
                      placeholder="Search states..."
                    />

                    {/* Sort By */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Sort By
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => handleFilterChange('sort', e.target.value)}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="name">Name</option>
                        <option value="type">Type</option>
                        <option value="state">State</option>
                        <option value="quality">Quality Score</option>
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Category Tags */}
              {browseCategories.length > 0 && (
                <Card className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 mb-3">
                      <Building2 className="w-4 h-4" />
                      Top Categories
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {browseCategories.slice(0, 10).map(cat => (
                        <button
                          key={cat.name}
                          onClick={() => handleFilterChange('orgType', selectedOrgType === cat.name ? '' : cat.name)}
                          className={`px-2 py-1 text-xs rounded-full transition-colors ${
                            selectedOrgType === cat.name
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {cat.name} <span className="opacity-60">({cat.count.toLocaleString()})</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Coverage Summary */}
              {Object.keys(browseCoverage).length > 0 && (
                <Card className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 mb-3">
                      <Globe className="w-4 h-4" />
                      State Coverage
                    </h3>
                    <div className="grid grid-cols-3 gap-1 text-xs max-h-64 overflow-y-auto">
                      {Object.entries(browseCoverage)
                        .sort(([,a], [,b]) => b - a)
                        .map(([state, count]) => (
                          <button
                            key={state}
                            onClick={() => handleFilterChange('state', selectedState === state ? '' : state)}
                            className={`px-1.5 py-1 rounded text-center transition-colors ${
                              selectedState === state
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                          >
                            <span className="font-medium">{state}</span>
                            <span className="opacity-60 block">{count.toLocaleString()}</span>
                          </button>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9">
            {searchError && (
              <ErrorState
                title="Search Error"
                message={searchError}
                onRetry={() => {
                  setSearchError(null);
                  if (hasActiveFilters || searchMode !== 'browse') {
                    loadBrowseProviders(1, selectedOrgType, selectedService, selectedState, sortBy);
                  }
                }}
                retryLabel="Retry"
                className="mb-6"
              />
            )}

            {/* Landing view — shown when no search/filter has been applied yet */}
            {providers.length === 0 && !isSearching && !searchError && searchMode === 'browse' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 md:p-12 text-center border border-gray-200 dark:border-gray-700">
                <div className="max-w-lg mx-auto space-y-4">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto">
                    <Search className="w-8 h-8 md:w-10 md:h-10 text-blue-600 dark:text-blue-300" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Search {browseOverallTotal > 0 ? browseOverallTotal.toLocaleString() : ''} Providers & CBOs
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Use the smart search above, apply filters on the left, or search by location to find providers.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4 text-left">
                    {browseCategories.slice(0, 6).map(cat => (
                      <button
                        key={cat.name}
                        onClick={() => handleFilterChange('orgType', cat.name)}
                        className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{cat.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{cat.count.toLocaleString()} providers</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 pt-2">
                    Click a category above or use the filters to start browsing
                  </p>
                </div>
              </div>
            )}

            {/* Results view — shown after search/filter */}
            {(providers.length > 0 || isSearching || searchMode !== 'browse') && (
              <>
                {/* Results Header Bar */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                          viewMode === 'list'
                            ? 'bg-blue-600 dark:bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        <List className="w-4 h-4" />
                        <span className="hidden sm:inline">List</span>
                      </button>
                      <button
                        onClick={() => setViewMode('map')}
                        className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                          viewMode === 'map'
                            ? 'bg-blue-600 dark:bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        <Map className="w-4 h-4" />
                        <span className="hidden sm:inline">Map</span>
                      </button>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {isSearching ? (
                        <span className="flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</span>
                      ) : searchMode === 'browse' ? (
                        `${browseTotal.toLocaleString()} providers (page ${browsePage} of ${browseTotalPages})`
                      ) : (
                        `${providers.length} ${providers.length === 1 ? 'result' : 'results'}`
                      )}
                    </span>
                  </div>

                  {/* Active filter pills */}
                  {hasActiveFilters && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedOrgType && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                          {selectedOrgType}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('orgType', '')} />
                        </span>
                      )}
                      {selectedService && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                          {selectedService}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('service', '')} />
                        </span>
                      )}
                      {selectedState && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                          {selectedState}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('state', '')} />
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Results Display */}
                {viewMode === 'list' ? (
                  <ProviderResults
                    providers={providers}
                    isLoading={isSearching}
                    onProviderClick={handleProviderClick}
                    onGetDirections={handleGetDirections}
                    onSaveProvider={handleSaveProvider}
                    isProviderSaved={isProviderSaved}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
                    <ProviderMap
                      providers={providers}
                      center={searchLocation || undefined}
                      zoom={searchMode === 'location' ? 12 : 4}
                      onProviderClick={handleProviderClick}
                    />
                  </div>
                )}
              </>
            )}

            {/* Pagination - only in browse mode */}
            {searchMode === 'browse' && browseTotalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(browsePage - 1)}
                  disabled={browsePage <= 1}
                  className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>

                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, browseTotalPages) }, (_, i) => {
                    let pageNum: number;
                    if (browseTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (browsePage <= 3) {
                      pageNum = i + 1;
                    } else if (browsePage >= browseTotalPages - 2) {
                      pageNum = browseTotalPages - 4 + i;
                    } else {
                      pageNum = browsePage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-9 h-9 text-sm font-medium rounded-md ${
                          pageNum === browsePage
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(browsePage + 1)}
                  disabled={browsePage >= browseTotalPages}
                  className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Provider Detail Modal */}
      {selectedProvider && (
        <ProviderDetail
          provider={selectedProvider}
          onClose={handleCloseDetail}
          onGetDirections={handleGetDirections}
          onSelectProvider={onSelectProvider}
          showSelectButton={showSelectButton}
          onSaveProvider={handleSaveProvider}
          isProviderSaved={isProviderSaved(selectedProvider.id)}
        />
      )}

      {/* Saved Notification */}
      {showSavedNotification && (
        <div className="fixed bottom-8 right-8 z-50 animate-slide-up">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center space-x-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <div className="font-semibold">Provider Saved!</div>
              <div className="text-sm text-green-100">Added to My Providers</div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-1">
              Provider information is regularly updated from verified sources including HRSA, SAMHSA, CMS, and IRS.
            </p>
            <p>
              Please contact providers directly to verify services, hours, and insurance acceptance.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
